import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Archive,
  Activity,
  Info,
  Phone,
  Mail,
  Globe,
  MessageCircle,
  FileText,
  FolderOpen,
  Search,
  Package,
  Play,
  PenLine,
} from 'lucide-react';
import { clientsAPI, postsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, Button, StatusBadge } from '../ui';
import Loading from '../common/Loading';
import ClientPackagesSection from './ClientPackagesSection';
import ClientMediaSection from './ClientMediaSection';
import ClientSeoSection from './ClientSeoSection';
import EditClientModal from './EditClientModal';
import { formatDate, handleApiError, getImageUrl, showToast, showConfirm, getMediaTypeFromUrl } from '../../utils/helpers';
import { POST_STATUS } from '../../utils/constants';
import { PLATFORMS } from '../../utils/platformConfig';
import { PlatformIcon } from '../icons/PlatformIcons';

const ClientPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isDesigner, isWriter, isSeoSpecialist } = useAuth();
  const { isRTL, t } = useLanguage();
  const [client, setClient] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [archiving, setArchiving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');

  useEffect(() => {
    fetchClientData();
  }, [id]);

  const fetchClientData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [clientResponse, postsResponse] = await Promise.all([
        clientsAPI.getById(id),
        clientsAPI.getPosts(id),
      ]);

      setClient(clientResponse.data.client);
      setPosts(postsResponse.data.posts);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    const confirmed = await showConfirm(
      isRTL ? 'هل أنت متأكد من أرشفة هذا العميل؟' : 'Are you sure you want to archive this client?',
      isRTL
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setArchiving(true);
      await clientsAPI.archive(id);
      showToast.success(isRTL ? 'تم أرشفة العميل بنجاح!' : 'Client archived successfully!');
      navigate('/settings');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setArchiving(false);
    }
  };

  const handleApprovePost = async (postId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const confirmed = await showConfirm(
      isRTL ? 'هل أنت متأكد من تعميد هذا البوست؟' : 'Are you sure you want to approve this post?',
      isRTL
    );

    if (!confirmed) return;

    try {
      await postsAPI.approve(postId);
      await fetchClientData();
      showToast.success(isRTL ? 'تم تعميد البوست بنجاح!' : 'Post approved successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    }
  };

  const handleDeletePost = async (postId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const confirmed = await showConfirm(
      isRTL ? 'هل أنت متأكد من حذف هذا البوست؟' : 'Are you sure you want to delete this post?',
      isRTL
    );

    if (!confirmed) return;

    try {
      await postsAPI.delete(postId);
      await fetchClientData();
      showToast.success(isRTL ? 'تم حذف البوست بنجاح!' : 'Post deleted successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    }
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error) {
    return (
      <div className="text-center py-12" dir={isRTL ? 'rtl' : 'ltr'}>
        <p className="text-red-600">{error}</p>
        <Link to="/clients" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
          {t('backToClients')}
        </Link>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12" dir={isRTL ? 'rtl' : 'ltr'}>
        <p className="text-gray-600">{t('clientNotFound')}</p>
      </div>
    );
  }

  const filteredPosts = filterStatus === 'all'
    ? posts
    : posts.filter((post) => post.status === filterStatus);

  const statusCounts = {
    all: posts.length,
    pending: posts.filter((p) => p.status === POST_STATUS.PENDING).length,
    needs_changes: posts.filter((p) => p.status === POST_STATUS.NEEDS_CHANGES).length,
    approved: posts.filter((p) => p.status === POST_STATUS.APPROVED).length,
  };

  const totalPosts = client.total_posts || 0;
  const approvedPosts = client.approved_posts || 0;
  const pendingPosts = client.pending_posts || 0;
  const needsChangesPosts = client.needs_changes_posts || 0;
  const approvalRate = totalPosts > 0 ? Math.round((approvedPosts / totalPosts) * 100) : 0;

  const articles = posts.filter((p) => p.content_type === 'blog');

  const tabs = [
    { id: 'posts', label: isRTL ? 'المنشورات' : 'Posts', icon: FileText, count: posts.length, show: true },
    { id: 'articles', label: isRTL ? 'المقالات' : 'Articles', icon: PenLine, count: articles.length, show: true },
    { id: 'media', label: isRTL ? 'مكتبة الوسائط' : 'Media Library', icon: FolderOpen, show: isAdmin || isDesigner || isWriter },
    { id: 'seo', label: isRTL ? 'خطط SEO' : 'SEO Plans', icon: Search, show: isAdmin || isSeoSpecialist },
    { id: 'packages', label: isRTL ? 'الباقات' : 'Packages', icon: Package, show: isAdmin },
  ].filter((t) => t.show);

  const effectiveTab = tabs.some((t) => t.id === activeTab) ? activeTab : (tabs[0]?.id || 'posts');

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header with Logo & Summary */}
      <Card className="p-5 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
        <div
          className={`flex items-start justify-between ${
            ''
          }`}
        >
          <div
            className={`flex items-start gap-4 ${
              ''
            }`}
          >
            {/* Logo */}
            {client.logo_url && (
              <div className="flex-shrink-0">
                <img
                  src={getImageUrl(client.logo_url)}
                  alt={client.company_name}
                  className="w-20 h-20 rounded-lg object-cover border-2 border-slate-200 bg-white"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}

            <div className={isRTL ? 'text-right' : 'text-left'}>
              <Link
                to="/clients"
                className="text-xs text-sky-600 hover:text-sky-700 mb-1 inline-flex items-center gap-1"
              >
                {isRTL ? 'العودة للعملاء' : 'Back to clients'}
              </Link>
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">
                {client.company_name}
              </h1>
              {client.description && (
                <p className="text-slate-600 mt-1 text-sm">
                  {client.description}
                </p>
              )}

              {/* Small summary badges */}
              <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-600">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/70 border border-slate-200">
                  <Activity className="w-3 h-3 text-sky-500" />
                  {t('totalPosts')}: {totalPosts}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700">
                  ✅ {t('approvedLabel')}: {approvedPosts}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 border border-amber-100 text-amber-700">
                  ⏳ {t('pendingLabel')}: {pendingPosts}
                </span>
              </div>
            </div>
          </div>

          {isAdmin && (
            <div
              className={`flex items-center gap-2 ${
                ''
              }`}
            >
              {!client.is_archived && (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => setShowEditModal(true)}
                  >
                    ✏️ {t('edit')}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleArchive}
                    disabled={archiving}
                  >
                    <Archive className="h-4 w-4" />
                    {t('archive')}
                  </Button>
                </>
              )}
              <Link to={`/posts/new?clientId=${client.id}`}>
                <Button
                  variant="primary"
                  className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-sm"
                >
                  {t('createNewPost')}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </Card>

      {/* Client Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client Info Card */}
        <Card className="p-6">
          <div
            className={`flex items-center justify-between mb-4 ${
              ''
            }`}
          >
            <h2
              className={`text-sm font-semibold text-slate-700 tracking-wide uppercase ${
                isRTL ? 'text-right' : 'text-left'
              }`}
            >
              {t('companyInformation')}
            </h2>
            <Info className="w-4 h-4 text-slate-400" />
          </div>
          <div className="space-y-4 text-sm">
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <p className="text-slate-500">{t('companyName')}</p>
              <p className="font-semibold text-slate-900">
                {client.company_name}
              </p>
            </div>
            {client.description && (
              <div className={isRTL ? 'text-right' : 'text-left'}>
                <p className="text-slate-500">{t('description')}</p>
                <p className="font-medium text-slate-900">
                  {client.description}
                </p>
              </div>
            )}
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <p className="text-slate-500">{t('createdLabel')}</p>
              <p className="font-medium text-slate-900">
                {formatDate(client.created_at)}
              </p>
            </div>
            {client.is_active !== undefined && (
              <div className={isRTL ? 'text-right' : 'text-left'}>
                <p className="text-slate-500">{t('status')}</p>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    client.is_active
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                      : 'bg-rose-50 text-rose-700 border border-rose-100'
                  }`}
                >
                  <span
                    className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${
                      client.is_active ? 'bg-emerald-500' : 'bg-rose-500'
                    }`}
                  />
                  {client.is_active ? t('active') : t('inactive')}
                </span>
              </div>
            )}
          </div>
        </Card>

        {/* Contact Information Card */}
        <Card className="p-6">
          <div
            className={`flex items-center justify-between mb-4 ${
              ''
            }`}
          >
            <h2
              className={`text-sm font-semibold text-slate-700 tracking-wide uppercase ${
                isRTL ? 'text-right' : 'text-left'
              }`}
            >
              {t('contactInformation')}
            </h2>
            <Phone className="w-4 h-4 text-slate-400" />
          </div>
          <div className="space-y-4 text-sm">
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <p className="text-slate-500">{t('contactLabel')}</p>
              <p className="font-semibold text-slate-900">
                {client.contact_name || t('notSet')}
              </p>
            </div>
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <p className="text-slate-500">{t('emailLabel')}</p>
              <p className="font-medium text-slate-900 break-all">
                {client.contact_email ? (
                  <a
                    href={`mailto:${client.contact_email}`}
                    className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700"
                  >
                    <Mail className="w-3 h-3" />
                    {client.contact_email}
                  </a>
                ) : (
                  <span className="text-slate-400">{t('notSet')}</span>
                )}
              </p>
            </div>
            {client.phone && (
              <div className={isRTL ? 'text-right' : 'text-left'}>
                <p className="text-slate-500">{t('phoneNumber')}</p>
                <p className="font-medium text-slate-900">
                  <a
                    href={`tel:${client.phone}`}
                    className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700"
                  >
                    <Phone className="w-3 h-3" />
                    {client.phone}
                  </a>
                </p>
              </div>
            )}
            {client.whatsapp_number && (
              <div className={isRTL ? 'text-right' : 'text-left'}>
                <p className="text-slate-500">{t('whatsappNumber')}</p>
                <p className="font-medium text-slate-900">
                  <a
                    href={`https://wa.me/${client.whatsapp_number.replace(
                      /[^0-9]/g,
                      ''
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 ${
                      ''
                    }`}
                  >
                    <MessageCircle className="w-3 h-3" />
                    <span>{client.whatsapp_number}</span>
                  </a>
                </p>
              </div>
            )}
            {client.website && (
              <div className={isRTL ? 'text-right' : 'text-left'}>
                <p className="text-slate-500">{t('website')}</p>
                <p className="font-medium text-slate-900">
                  <a
                    href={
                      client.website.startsWith('http')
                        ? client.website
                        : `https://${client.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700"
                  >
                    <Globe className="w-3 h-3" />
                    {client.website}
                  </a>
                </p>
              </div>
            )}
            {client.social_media_accounts && Object.keys(client.social_media_accounts).length > 0 && (
              <div>
                <p className={`text-sm text-gray-600 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>{t('socialMediaLabel')}</p>
                <div className="flex flex-wrap gap-2">
                  {client.social_media_accounts.instagram && (
                    <a
                      href={`https://instagram.com/${client.social_media_accounts.instagram.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-pink-100 text-pink-700 rounded text-sm hover:bg-pink-200"
                    >
                      <span>📷</span>
                      <span>{client.social_media_accounts.instagram}</span>
                    </a>
                  )}
                  {client.social_media_accounts.twitter && (
                    <a
                      href={`https://twitter.com/${client.social_media_accounts.twitter.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                    >
                      <span>🐦</span>
                      <span>{client.social_media_accounts.twitter}</span>
                    </a>
                  )}
                  {client.social_media_accounts.facebook && (
                    <a
                      href={`https://facebook.com/${client.social_media_accounts.facebook}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                    >
                      <span>👥</span>
                      <span>{client.social_media_accounts.facebook}</span>
                    </a>
                  )}
                  {client.social_media_accounts.snapchat && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-sm">
                      <span>👻</span>
                      <span>{client.social_media_accounts.snapchat}</span>
                    </span>
                  )}
                  {client.social_media_accounts.tiktok && (
                    <a
                      href={`https://tiktok.com/@${client.social_media_accounts.tiktok.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                    >
                      <span>🎵</span>
                      <span>{client.social_media_accounts.tiktok}</span>
                    </a>
                  )}
                  {client.social_media_accounts.linkedin && (
                    <a
                      href={`https://linkedin.com/company/${client.social_media_accounts.linkedin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                    >
                      <span>💼</span>
                      <span>{client.social_media_accounts.linkedin}</span>
                    </a>
                  )}
                  {client.social_media_accounts.youtube && (
                    <a
                      href={`https://youtube.com/@${client.social_media_accounts.youtube.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                    >
                      <span>▶️</span>
                      <span>{client.social_media_accounts.youtube}</span>
                    </a>
                  )}
                </div>
              </div>
            )}
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <p className="text-slate-500">
                {isRTL ? 'مدير الحساب' : 'Account Manager'}
              </p>
              <p className="font-medium text-slate-900">
                {client.admin_name || (isRTL ? 'غير معين' : 'Not assigned')}
              </p>
            </div>
          </div>
        </Card>

        {/* Posts Statistics Card */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-700 tracking-wide uppercase">
              {t('postsStatistics')}
            </h2>
            <Activity className="w-4 h-4 text-slate-400" />
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{t('totalPosts')}</span>
                <span className="text-2xl font-bold text-gray-900">{totalPosts}</span>
              </div>
            </div>

            {totalPosts > 0 && (
              <>
          <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">{t('approvalRate')}</span>
                    <span className="text-sm font-medium text-gray-900">{approvalRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all"
                      style={{ width: `${approvalRate}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-2 pt-2 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-green-500"></span>
                      <span className="text-sm text-gray-600">{t('approvedLabel')}</span>
                    </div>
                    <span className="font-medium text-gray-900">{approvedPosts}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                      <span className="text-sm text-gray-600">{t('pendingLabel')}</span>
                    </div>
                    <span className="font-medium text-gray-900">{pendingPosts}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-red-500"></span>
                      <span className="text-sm text-gray-600">{t('needsChangesLabel')}</span>
                    </div>
                    <span className="font-medium text-gray-900">{needsChangesPosts}</span>
                  </div>
                </div>
              </>
            )}

            {totalPosts === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                {t('noPosts')}
              </p>
            )}
          </div>
        </Card>
        </div>

      {/* Tabs */}
      <Card className="overflow-hidden">
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-medium transition-colors whitespace-nowrap border-b-2 -mb-px ${
                  effectiveTab === tab.id
                    ? 'border-sky-500 text-sky-600 bg-sky-50/50'
                    : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== undefined && (
                  <span
                    className={`ms-1 px-2 py-0.5 rounded-full text-xs ${
                      effectiveTab === tab.id ? 'bg-sky-200/80 text-sky-800' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="min-h-[200px]">
          {effectiveTab === 'packages' && (
            <div className="p-4">
              <ClientPackagesSection clientId={id} />
            </div>
          )}

          {effectiveTab === 'seo' && (
            <div className="p-4">
              <ClientSeoSection clientId={id} clientName={client.company_name} readOnly />
            </div>
          )}

          {effectiveTab === 'media' && (
            <div className="p-6">
              <ClientMediaSection clientId={id} clientName={client?.company_name} />
            </div>
          )}

          {effectiveTab === 'articles' && (
            <div className="p-4 pt-2">
              {articles.length === 0 ? (
                <div className="py-12 text-center">
                  <PenLine className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{isRTL ? 'لا توجد مقالات لهذا العميل' : 'No articles for this client'}</p>
                  {isAdmin && (
                    <Link
                      to={`/articles/new?clientId=${client.id}`}
                      className="inline-block mt-3 text-sky-600 hover:text-sky-700 font-medium text-sm"
                    >
                      {isRTL ? 'إنشاء مقالة' : 'Create article'}
                    </Link>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {articles.map((post) => (
                    <Link key={post.id} to={`/articles/${post.id}/edit`}>
                      <Card hover className="h-full flex flex-col border-slate-200 overflow-hidden">
                        <div className="aspect-video bg-slate-100 flex items-center justify-center overflow-hidden">
                          {post.current_image_url ? (
                            <img
                              src={getImageUrl(post.current_image_url)}
                              alt={post.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const p = e.target.parentElement;
                                if (p) { p.className = 'aspect-video bg-slate-100 flex items-center justify-center'; p.innerHTML = '<p class="text-slate-400 text-sm">' + (isRTL ? 'لا صورة' : 'No image') + '</p>'; }
                              }}
                            />
                          ) : (
                            <PenLine className="w-12 h-12 text-slate-300" />
                          )}
                        </div>
                        <div className="p-4 flex-1 flex flex-col">
                          <h3 className="font-semibold text-slate-900 line-clamp-2 mb-2">{post.title || (isRTL ? 'بدون عنوان' : 'Untitled')}</h3>
                          {post.description && (
                            <p className="text-sm text-slate-600 line-clamp-2 mb-3">{post.description}</p>
                          )}
                          <div className="mt-auto flex items-center justify-between">
                            <StatusBadge status={post.status} />
                            <span className="text-xs text-slate-500">{formatDate(post.created_at)}</span>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {effectiveTab === 'posts' && (
            <div className="p-4 pt-2">
      {/* Filter Pills */}
      <div
        className={`flex flex-wrap gap-2 ${
          isRTL ? 'justify-end' : 'justify-start'
        }`}
      >
        <button
          onClick={() => setFilterStatus('all')}
          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
            filterStatus === 'all'
              ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
          }`}
        >
          {t('allPostsFilter')} ({statusCounts.all})
        </button>
        <button
          onClick={() => setFilterStatus(POST_STATUS.PENDING)}
          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
            filterStatus === POST_STATUS.PENDING
              ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
          }`}
        >
          {t('pendingLabel')} ({statusCounts.pending})
        </button>
        <button
          onClick={() => setFilterStatus(POST_STATUS.NEEDS_CHANGES)}
          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
            filterStatus === POST_STATUS.NEEDS_CHANGES
              ? 'bg-rose-500 text-white border-rose-500 shadow-sm'
              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
          }`}
        >
          {t('needsChangesLabel')} ({statusCounts.needs_changes})
        </button>
        <button
          onClick={() => setFilterStatus(POST_STATUS.APPROVED)}
          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
            filterStatus === POST_STATUS.APPROVED
              ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
          }`}
        >
          {t('approvedLabel')} ({statusCounts.approved})
        </button>
      </div>

      {/* Posts List Header: title + view toggle */}
      {filteredPosts.length > 0 && (
        <div className="flex items-center justify-between mb-3 mt-4">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-900 text-white text-xs">
              📄
            </span>
            {isRTL ? 'منشورات العميل' : 'Client Posts'}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-md text-xs font-medium transition-all ${
                viewMode === 'grid'
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              {isRTL ? 'شبكة' : 'Grid'}
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md text-xs font-medium transition-all ${
                viewMode === 'list'
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              {isRTL ? 'قائمة' : 'List'}
            </button>
          </div>
        </div>
      )}

      {/* Posts List */}
      {filteredPosts.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <p className="text-gray-500">{t('noPostsFound')}</p>
            {isAdmin && (
              <Link
                to={`/posts/new?client=${client.id}`}
                className="inline-block mt-4 text-primary-600 hover:text-primary-700 font-medium"
              >
                {t('createFirstPost')}
              </Link>
            )}
          </div>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post) => {
            const platformInfo = post.platform ? PLATFORMS[post.platform] : null;
            return (
              <Link key={post.id} to={`/posts/${post.id}`}>
                <Card hover className="h-full flex flex-col border-slate-200 bg-white/90 shadow-sm hover:shadow-md rounded-2xl overflow-hidden transition-shadow">
                  {/* Post Image/Video */}
                  <div className="relative">
                    {post.current_image_url ? (
                      <div className="aspect-video bg-slate-100 rounded-t-xl overflow-hidden relative group">
                        {getMediaTypeFromUrl(post.current_image_url) === 'video' ? (
                          <>
                            <video
                              src={getImageUrl(post.current_image_url)}
                              className="w-full h-full object-cover"
                              muted
                              playsInline
                              preload="metadata"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.className =
                                  'aspect-video bg-slate-100 rounded-t-xl flex items-center justify-center';
                                e.target.parentElement.innerHTML =
                                  '<p class="text-slate-400 text-sm">No video</p>';
                              }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-opacity">
                              <div className="bg-white/90 rounded-full p-3 shadow-lg">
                                <Play className="w-8 h-8 text-slate-900" />
                              </div>
                            </div>
                          </>
                        ) : (
                          <img
                            src={getImageUrl(post.current_image_url)}
                            alt={post.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.className =
                                'aspect-video bg-slate-100 rounded-t-xl flex items-center justify-center';
                              e.target.parentElement.innerHTML =
                                '<p class="text-slate-400 text-sm">No image</p>';
                            }}
                          />
                        )}
                      </div>
                    ) : (
                      <div className="aspect-video bg-slate-100 rounded-t-xl flex items-center justify-center">
                        <p className="text-slate-400 text-sm">{t('noImage')}</p>
                      </div>
                    )}

                    {/* Platform badge */}
                    {platformInfo && (
                      <div
                        className={`absolute top-3 ${
                          isRTL ? 'left-3' : 'right-3'
                        } flex items-center justify-center w-8 h-8 rounded-lg bg-black/55 backdrop-blur-md shadow-md`}
                        title={isRTL ? platformInfo.nameAr : platformInfo.name}
                      >
                        <PlatformIcon
                          platformId={platformInfo.id}
                          className="w-4 h-4 text-white"
                        />
                      </div>
                    )}
                  </div>

                  {/* Post Info */}
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <h3 className="font-semibold text-gray-900 line-clamp-1">{post.title}</h3>
                      <StatusBadge status={post.status} />
                    </div>

                    {post.description && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                        {post.description}
                      </p>
                    )}

                    <div className="mt-auto space-y-2">
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span className="truncate">{formatDate(post.created_at)}</span>
                        <div
                          className={`flex items-center gap-2 ${
                            isRTL ? 'flex-row-reverse' : ''
                          }`}
                        >
                          {post.comment_count > 0 && <span>💬 {post.comment_count}</span>}
                          {post.version_count > 1 && (
                            <span>🔄 v{post.current_version}</span>
                          )}
                        </div>
                      </div>
                      {isAdmin && (
                        <div
                          className={`flex flex-wrap items-center gap-2 text-xs mt-2 ${
                            isRTL ? 'justify-end' : 'justify-start'
                          }`}
                        >
                          {post.status === POST_STATUS.PENDING && (
                            <button
                              type="button"
                              onClick={(e) => handleApprovePost(post.id, e)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium border border-emerald-200 hover:bg-emerald-100 transition-colors"
                              title={isRTL ? 'تعميد البوست' : 'Approve post'}
                            >
                              <span>✓</span>
                              <span>{isRTL ? 'تعميد' : 'Approve'}</span>
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={(e) => handleDeletePost(post.id, e)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 font-medium border border-rose-200 hover:bg-rose-100 transition-colors"
                            title={isRTL ? 'حذف البوست' : 'Delete post'}
                          >
                            <span>🗑</span>
                            <span>{isRTL ? 'حذف' : 'Delete'}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPosts.map((post) => (
            <Link key={post.id} to={`/posts/${post.id}`} className="block">
              <Card hover className="p-4">
                <div className="flex items-center gap-4">
                  {/* Thumbnail */}
                  <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-slate-100">
                    {post.current_image_url ? (
                      getMediaTypeFromUrl(post.current_image_url) === 'video' ? (
                        <video
                          src={getImageUrl(post.current_image_url)}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                          preload="metadata"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.className =
                              'w-full h-full bg-slate-100 flex items-center justify-center';
                            e.target.parentElement.innerHTML =
                              '<p class="text-slate-400 text-xs">No video</p>';
                          }}
                        />
                      ) : (
                        <img
                          src={getImageUrl(post.current_image_url)}
                          alt={post.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.className =
                              'w-full h-full bg-slate-100 flex items-center justify-center';
                            e.target.parentElement.innerHTML =
                              '<p class="text-slate-400 text-xs">No image</p>';
                          }}
                        />
                      )
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-slate-400 text-xl">📄</span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <h4 className="font-semibold text-slate-900 truncate">
                          {post.title}
                        </h4>
                        {post.description && (
                          <p className="text-xs text-slate-600 truncate mt-1">
                            {post.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-1">
                          <span>{formatDate(post.created_at)}</span>
                          {post.comment_count > 0 && <span>• 💬 {post.comment_count}</span>}
                          {post.version_count > 1 && (
                            <span>• 🔄 v{post.current_version}</span>
                          )}
                        </div>
                      </div>
                      <StatusBadge status={post.status} />
                    </div>

                    {isAdmin && (
                      <div
                        className={`flex flex-wrap items-center gap-2 text-xs mt-2 ${
                          isRTL ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        {post.status === POST_STATUS.PENDING && (
                          <button
                            type="button"
                            onClick={(e) => handleApprovePost(post.id, e)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium border border-emerald-200 hover:bg-emerald-100 transition-colors"
                            title={isRTL ? 'تعميد البوست' : 'Approve post'}
                          >
                            <span>✓</span>
                            <span>{isRTL ? 'تعميد' : 'Approve'}</span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => handleDeletePost(post.id, e)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 font-medium border border-rose-200 hover:bg-rose-100 transition-colors"
                          title={isRTL ? 'حذف البوست' : 'Delete post'}
                        >
                          <span>🗑</span>
                          <span>{isRTL ? 'حذف' : 'Delete'}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
            </div>
          )}
        </div>
      </Card>

      {/* Edit Client Modal */}
      <EditClientModal
        client={client}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={fetchClientData}
      />
    </div>
  );
};

export default ClientPage;


